import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api } from "../api";
import { relative } from "../components/ui";
import { CertificatesPanel, ContainersPanel, DatabasePanel, Findings, HostPanel } from "./health/parts";
import { gib, pct, type LabHealth } from "./health/types";

// The question: is the lab itself healthy right now — its host, database, containers and certificates?

function headline(h: LabHealth): string {
  if (h.status === "ok") {
    const c = h.containers.available ? `${h.containers.projects.length} projects, ${h.containers.healthy} containers healthy` : "host, database and certificates healthy";
    return `All good: ${c}.`;
  }
  const crit = h.findings.filter((f) => f.severity === "critical").length;
  const warn = h.findings.filter((f) => f.severity === "warning").length;
  const counts = [crit && `${crit} critical`, warn && `${warn} warning${warn === 1 ? "" : "s"}`].filter(Boolean).join(", ");
  return `${counts}: ${h.findings[0]?.title ?? ""}.`;
}

function Kpi({ label, value, unit, note }: { label: string; value: ReactNode; unit?: string; note: ReactNode }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value num">{value}{unit && <span className="unit">{unit}</span>}</div>
      <div className="kpi-note">{note}</div>
    </div>
  );
}

function Kpis({ h }: { h: LabHealth }) {
  const { cpu, memory, disk } = h.host;
  const db = h.database.available ? h.database : null;
  const dbm = db?.metrics.available ? db.metrics : null;
  const freeStorage = dbm?.freeStorageBytes.latest ?? null;
  return (
    <div className="kpis">
      <Kpi label="Host CPU now" value={cpu.available ? pct(cpu.latest) : "—"} note={cpu.available ? `peak ${pct(cpu.max)} in the last hour` : "not read"} />
      <Kpi
        label="Memory used"
        value={memory.available ? pct(((memory.totalBytes - memory.availableBytes) / memory.totalBytes) * 100) : "—"}
        note={memory.available ? `${gib(memory.availableBytes)} available of ${gib(memory.totalBytes)}` : "read on the host"}
      />
      <Kpi
        label="Disk used"
        value={disk.available ? pct((disk.usedBytes / disk.sizeBytes) * 100) : "—"}
        note={disk.available ? `${gib(disk.freeBytes)} free of ${gib(disk.sizeBytes)}` : "read on the host"}
      />
      <Kpi label="Database CPU" value={dbm ? pct(dbm.cpu.latest) : "—"} note={dbm ? `peak ${pct(dbm.cpu.max)} in the last hour` : "not read"} />
      <Kpi
        label="Database free storage"
        value={freeStorage !== null ? (freeStorage / 1024 ** 3).toFixed(1) : "—"}
        unit={freeStorage !== null ? "GiB" : undefined}
        note={freeStorage !== null && db?.allocatedGiB ? `${pct((freeStorage / (db.allocatedGiB * 1024 ** 3)) * 100)} of ${db.allocatedGiB} GiB` : "not read"}
      />
      <Kpi
        label="Containers healthy"
        value={h.containers.available ? h.containers.healthy : "—"}
        unit={h.containers.available ? `of ${h.containers.expected}` : undefined}
        note={h.containers.available ? `${h.containers.total - h.containers.expected} stopped on purpose` : "read on the host"}
      />
    </div>
  );
}

function Loading() {
  // The final layout with each value a flat grey bar at its real size; nothing moves when data lands.
  const bar = (w: number | string, h = 14) => <span className="skeleton" style={{ width: w, height: h }} />;
  const rows = (n: number, cols: number[]) => (
    <table><tbody>{Array.from({ length: n }, (_, i) => <tr key={i}>{cols.map((w, j) => <td key={j}>{bar(w)}</td>)}</tr>)}</tbody></table>
  );
  const panel = (title: string, body: ReactNode) => <section className="panel flush"><div className="panel-head"><h2>{title}</h2></div>{body}</section>;
  const side = (title: string) => panel(title, <div style={{ padding: "12px 20px 20px", display: "grid", gap: 16 }}>{bar("100%", 56)}{rows(6, [80, 180])}</div>);
  return (
    <div className="canvas" aria-busy="true">
      <div className="title-row"><div><h1>Lab health</h1><p className="sub">{bar(360, 16)}</p></div></div>
      <div className="kpis">
        {["Host CPU now", "Memory used", "Disk used", "Database CPU", "Database free storage", "Containers healthy"].map((l) => (
          <div className="kpi" key={l}><div className="kpi-label">{l}</div><div className="kpi-value">{bar(56, 28)}</div><div className="kpi-note">{bar(120, 12)}</div></div>
        ))}
      </div>
      <div className="split">
        <div style={{ display: "grid", gap: 24, alignContent: "start" }}>
          {panel("Findings", <div className="list">{[0, 1, 2].map((i) => <div className="item" key={i}><div>{bar(280)}</div></div>)}</div>)}
          {panel("Containers", rows(8, [160, 100, 30, 120]))}
          {panel("Certificates", rows(5, [200, 140, 100, 60]))}
        </div>
        <div style={{ display: "grid", gap: 24, alignContent: "start" }}>
          {side("Database")}
          {side("Host")}
        </div>
      </div>
    </div>
  );
}

export function HealthPage() {
  const q = useQuery({ queryKey: ["lab-health"], queryFn: () => api<LabHealth>("/lab/health"), refetchInterval: 30_000 });

  if (q.isPending) return <Loading />;
  if (q.error) {
    return (
      <div className="canvas">
        <div className="title-row"><h1>Lab health</h1></div>
        <div className="panel">Couldn't load the lab's health: {q.error.message}. <button className="btn ghost" onClick={() => q.refetch()}>Retry</button></div>
      </div>
    );
  }
  const h = q.data;

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Lab health</h1>
          <p className="sub">{headline(h)}</p>
        </div>
        <div className="actions">
          <span className="stamp" title={new Date(h.checkedAt).toLocaleString()}>Checked {relative(h.checkedAt)} · {new Date(h.checkedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
          <button className="btn" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? "Checking…" : "Refresh"}</button>
        </div>
      </div>

      <Kpis h={h} />

      <div className="split">
        <div style={{ display: "grid", gap: 24, alignContent: "start" }}>
          <Findings h={h} />
          <ContainersPanel part={h.containers} />
          <CertificatesPanel part={h.certificates} />
        </div>
        <div style={{ display: "grid", gap: 24, alignContent: "start" }}>
          <DatabasePanel part={h.database} backups={h.backups} />
          <HostPanel host={h.host} backups={h.backups} />
        </div>
      </div>
    </div>
  );
}
